/**
 * DEPARTMENT MANAGER - APPROVAL QUEUE PAGE
 * 
 * Shows invoices that need approval from this department manager:
 * 1. My Tasks - Invoices currently assigned to me for approval
 * 2. Department Flow - All invoices in my department's approval flow
 * 3. Ability to "take" tasks that need bypass
 *
 * Features: search, column sorting, summary cards, urgency indicators
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import {
  CheckCircle, Clock, AlertCircle, User, FileText, Eye,
  Search, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw,
  DollarSign, AlertTriangle, Inbox
} from 'lucide-react';
import InvoiceApprovalDetail from '../components/InvoiceApprovalDetail';
import TablePagination from '../components/TablePagination';

const DAQ_PAGE_SIZE = 25;

const DepartmentApprovalQueue = () => {
  const { hasPermission } = usePermissions();
  const [myTasks, setMyTasks] = useState([]);
  const [departmentFlow, setDepartmentFlow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('my-tasks');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('InvoiceDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [myTasksPage, setMyTasksPage] = useState(1);
  const [deptFlowPage, setDeptFlowPage] = useState(1);

  useEffect(() => {
    loadApprovalQueue();
  }, []);

  if (!hasPermission('approve_invoices')) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-semibold">Access Denied</p>
        <p className="text-gray-500 mt-2">You do not have permission to view the approval queue.</p>
      </div>
    );
  }

  const loadApprovalQueue = async () => {
    setLoading(true);
    setError('');

    try {
      const [myTasksRes, deptFlowRes] = await Promise.all([
        apiClient.get('/approvals/my-queue'),
        apiClient.get('/approvals/department-flow')
      ]);

      setMyTasks(myTasksRes.data.invoices || []);
      setDepartmentFlow(deptFlowRes.data.invoices || []);
    } catch (err) {
      console.error('Failed to load approval queue:', err);
      setError(err.response?.data?.message || 'Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeTask = async (invoiceId) => {
    if (!confirm('Assign this invoice to yourself for approval?')) return;

    try {
      await apiClient.post(`/approvals/${invoiceId}/take`, {});
      alert('Invoice assigned to you!');
      loadApprovalQueue();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to take invoice');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount || 0);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysPending = (dateStr) => {
    if (!dateStr) return 0;
    const diff = Date.now() - new Date(dateStr).getTime();
    return Math.max(0, Math.floor(diff / 86400000));
  };

  const getUrgencyBadge = (days) => {
    if (days <= 2) return <span className="text-green-600 text-xs font-medium">{days}d</span>;
    if (days <= 5) return <span className="text-yellow-600 text-xs font-semibold">{days}d</span>;
    return (
      <span className="inline-flex items-center text-red-600 text-xs font-bold">
        <AlertTriangle className="w-3 h-3 mr-0.5" />
        {days}d
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Pending Approval': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'Approved': 'bg-green-100 text-green-800 border border-green-200',
      'Rejected': 'bg-red-100 text-red-800 border border-red-200',
      'Processing': 'bg-blue-100 text-blue-800 border border-blue-200',
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800 border border-gray-200'}`}>
        {status}
      </span>
    );
  };

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Filter + sort logic
  const filterAndSort = useCallback((invoices) => {
    let filtered = invoices;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = invoices.filter(inv =>
        (inv.InvoiceNumber || '').toLowerCase().includes(q) ||
        (inv.VendorName || '').toLowerCase().includes(q) ||
        (inv.CurrentApproverName || '').toLowerCase().includes(q)
      );
    }
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'TotalAmount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'InvoiceDate') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [searchTerm, sortField, sortDirection]);

  const filteredMyTasks = useMemo(() => filterAndSort(myTasks), [myTasks, filterAndSort]);
  const filteredDeptFlow = useMemo(() => filterAndSort(departmentFlow), [departmentFlow, filterAndSort]);
  // Reset pages on search/sort change
  useEffect(() => { setMyTasksPage(1); setDeptFlowPage(1); }, [searchTerm, sortField, sortDirection]);

  // Summary stats
  const currentList = activeTab === 'my-tasks' ? myTasks : departmentFlow;
  const totalValue = currentList.reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
  const overdueCount = currentList.filter(i => getDaysPending(i.InvoiceDate) > 5).length;
  const mainCurrency = currentList[0]?.Currency || 'GBP';

  const renderInvoiceTable = (invoices, showTakeButton = false, page, setPage) => {
    const paged = invoices.slice((page - 1) * DAQ_PAGE_SIZE, page * DAQ_PAGE_SIZE);
    return (
    <>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('InvoiceNumber')}>
              <span className="inline-flex items-center">Invoice # <SortIcon field="InvoiceNumber" /></span>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('VendorName')}>
              <span className="inline-flex items-center">Vendor <SortIcon field="VendorName" /></span>
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalAmount')}>
              <span className="inline-flex items-center justify-end">Amount <SortIcon field="TotalAmount" /></span>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('InvoiceDate')}>
              <span className="inline-flex items-center">Date <SortIcon field="InvoiceDate" /></span>
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Waiting
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}>
              <span className="inline-flex items-center">Status <SortIcon field="Status" /></span>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Approver
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {invoices.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-4 py-12 text-center">
                <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">
                  {searchTerm ? 'No invoices match your search' : 'No invoices pending'}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm ? 'Try adjusting your search terms' : "You're all caught up!"}
                </p>
              </td>
            </tr>
          ) : (
            paged.map((invoice) => {
              const daysPending = getDaysPending(invoice.InvoiceDate);
              const isOverdue = daysPending > 5;
              return (
                <tr
                  key={invoice.InvoiceID}
                  onClick={() => setSelectedInvoiceId(invoice.InvoiceID)}
                  className={`cursor-pointer transition-colors ${isOverdue ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm font-medium text-blue-700">{invoice.InvoiceNumber}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{invoice.VendorName}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(invoice.TotalAmount, invoice.Currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{formatDate(invoice.InvoiceDate)}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {getUrgencyBadge(daysPending)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(invoice.Status)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-700">{invoice.CurrentApproverName || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                      {showTakeButton && (
                        <button
                          onClick={() => handleTakeTask(invoice.InvoiceID)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                          title="Assign to yourself"
                        >
                          <User className="w-3.5 h-3.5" />
                          Take
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedInvoiceId(invoice.InvoiceID)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Review
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
    <TablePagination page={page} pageSize={DAQ_PAGE_SIZE} total={invoices.length} onPageChange={setPage} />
    </>
  );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        <span className="ml-3 text-gray-500">Loading approval queue...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CheckCircle className="w-7 h-7 text-blue-600" />
            Invoice Approval Queue
          </h1>
          <p className="text-sm text-gray-500 mt-1">Review and approve invoices assigned to you</p>
        </div>
        <button
          onClick={loadApprovalQueue}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">My Tasks</p>
              <p className="text-2xl font-bold text-gray-900">{myTasks.length}</p>
              <p className="text-xs text-gray-400 mt-1">Assigned to you</p>
            </div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Department Flow</p>
              <p className="text-2xl font-bold text-gray-900">{departmentFlow.length}</p>
              <p className="text-xs text-gray-400 mt-1">In your department</p>
            </div>
            <FileText className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalValue, mainCurrency)}</p>
              <p className="text-xs text-gray-400 mt-1">Current tab</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Overdue (&gt;5 days)</p>
              <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{overdueCount}</p>
              <p className="text-xs text-gray-400 mt-1">Need attention</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => setActiveTab('my-tasks')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'my-tasks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
              My Tasks
              {myTasks.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">{myTasks.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('department-flow')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'department-flow'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
              Department Flow
              {departmentFlow.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">{departmentFlow.length}</span>
              )}
            </button>
          </nav>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search invoice, vendor..."
            className="pl-9 pr-3 py-2 w-64 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {activeTab === 'my-tasks' && renderInvoiceTable(filteredMyTasks, false, myTasksPage, setMyTasksPage)}
        {activeTab === 'department-flow' && renderInvoiceTable(filteredDeptFlow, true, deptFlowPage, setDeptFlowPage)}
      </div>

      {/* Showing count */}
      {searchTerm && (
        <div className="mt-2 text-xs text-gray-500">
          Showing {activeTab === 'my-tasks' ? filteredMyTasks.length : filteredDeptFlow.length} of{' '}
          {activeTab === 'my-tasks' ? myTasks.length : departmentFlow.length} invoices
        </div>
      )}

      {/* Invoice Approval Detail Modal */}
      {selectedInvoiceId && (
        <InvoiceApprovalDetail
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onActionComplete={() => {
            setSelectedInvoiceId(null);
            loadApprovalQueue();
          }}
        />
      )}
    </div>
  );
};

export default DepartmentApprovalQueue;
